import { Link } from "react-router-dom";
import { ArrowRight, Radar, Brain, Link2, Globe, Bell, Flag, Lock, Gauge, Users } from "lucide-react";
import { Seo } from "@/components/web/Seo";
import { MaskedLines, FadeUp } from "@/components/web/Reveal";

const FEATURES = [
  { icon: Radar, title: "Real-Time URL Scanning", body: "Submit any link and get a security verdict in seconds — no installs, no configuration." },
  { icon: Brain, title: "AI Phishing Detection", body: "Machine-learning models trained on scam and phishing patterns catch attacks that static blocklists miss." },
  { icon: Link2, title: "Redirect Chain Tracing", body: "Shortened or redirected links are followed to their true destination before judgment is passed." },
  { icon: Globe, title: "Domain Reputation Analysis", body: "Domain age, registration signals and lookalike characters are weighed against known threat intelligence." },
  { icon: Bell, title: "Plain-Language Verdicts", body: "Safe, suspicious, or dangerous — always with a human-readable explanation of the reasons." },
  { icon: Flag, title: "One-Click Reporting", body: "Flag malicious links to help protect the entire LinkExpln community." },
  { icon: Lock, title: "Privacy-First Scanning", body: "Scans are processed securely. We don't sell your data or track your browsing." },
  { icon: Gauge, title: "Built for Speed", body: "Dozens of security signals evaluated in parallel so verdicts arrive before doubt sets in." },
  { icon: Users, title: "Made for Everyone", body: "From your family group chat to your finance team — no security expertise required." },
];

export default function Features() {
  return (
    <>
      <Seo
        title="Features — LinkExpln"
        description="Explore LinkExpln features: real-time URL scanning, AI phishing detection, redirect tracing, domain reputation analysis and community threat reporting."
      />

      <section className="pt-24 pb-16 sm:pt-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">Capabilities</p>
          <h1
            data-testid="features-heading"
            className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]"
          >
            <MaskedLines lines={["EVERYTHING A LINK", "CAN HIDE — FOUND."]} />
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed">
            LinkExpln combines machine learning, threat intelligence and
            deep URL analysis into one effortless scan.
          </p>
        </div>
      </section>

      <section data-testid="features-grid" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.title} delay={(i % 3) * 0.08} className="bg-[#0A121D] p-8 sm:p-10 hover:bg-[#101A28] transition-colors duration-300 group">
                <f.icon size={26} className="text-[#00C5FE] mb-5 transition-transform duration-300 group-hover:-translate-y-1" />
                <h2 className="font-heading font-bold text-lg text-white">{f.title}</h2>
                <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{f.body}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-white max-w-2xl text-balance">
              See the shield at work.
            </h2>
            <Link
              to="/scanner"
              data-testid="features-cta-scanner"
              className="mt-8 inline-flex items-center gap-2 btn-brand text-black font-heading font-bold px-8 py-4 transition-colors duration-200"
            >
              Open the Scanner <ArrowRight size={18} strokeWidth={2.5} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
